import React from 'react';
import PropTypes from 'prop-types';
import { Modal } from '@mantine/core';
import moment from 'moment';
import 'moment/locale/id';
import styles from './styles.scoped.css';

export default function ModalWarningSubsidi({
  opened,
  onClose,
  onKoreksi,
  onConfirm,
  isLoading,
  type,
  title,
  description,
  startDate,
  endDate,
  previousEndDate,
  gapStartDate,
  gapEndDate,
  confirmText = 'Tambah Informasi Besaran Subsidi',
  koreksiText = 'Koreksi',
}) {
  const formatDateId = (dateStr) => {
    if (!dateStr) return '';
    const m = moment(dateStr).locale('id');
    return m.isValid() ? m.format('D MMMM YYYY') : dateStr;
  };

  const formatGapRange = (start, end) => {
    if (!start || !end) return '';
    const mStart = moment(start).locale('id');
    const mEnd = moment(end).locale('id');

    if (!mStart.isValid() || !mEnd.isValid()) return '';

    if (mStart.isSame(mEnd, 'day')) {
      return mStart.format('D MMMM YYYY');
    }

    if (mStart.isSame(mEnd, 'month') && mStart.isSame(mEnd, 'year')) {
      return `${mStart.format('D')}–${mEnd.format('D MMMM YYYY')}`;
    }

    if (mStart.isSame(mEnd, 'year')) {
      return `${mStart.format('D MMMM')} – ${mEnd.format('D MMMM YYYY')}`;
    }

    return `${mStart.format('D MMMM YYYY')} – ${mEnd.format('D MMMM YYYY')}`;
  };

  const renderContent = () => {
    if (title && description) {
      return {
        modalDescription: description,
        modalTitle: title,
      };
    }

    const formattedStartDate = formatDateId(startDate);
    const formattedEndDate = formatDateId(endDate);
    const formattedPreviousEndDate = formatDateId(previousEndDate);

    if (type === 'replace') {
      const formattedRange = formattedEndDate
        ? `${formattedStartDate} – ${formattedEndDate}`
        : formattedStartDate;

      return {
        modalDescription: (
          <span>
            Periode yang Anda tambahkan akan berlaku mulai{' '}
            <strong>{formattedRange}</strong> dan{' '}
            <strong>menggantikan besaran subsidi</strong> pada periode aktif yang
            memiliki rentang tanggal yang sama.
          </span>
        ),
        modalTitle: `Besaran Subsidi Baru Akan Berlaku Mulai ${formattedStartDate}`,
      };
    }

    if (type === 'replace_gap') {
      const formattedRange = formattedEndDate
        ? `${formattedStartDate} – ${formattedEndDate}`
        : formattedStartDate;

      const formattedGapRange = gapStartDate && gapEndDate
        ? `${formatDateId(gapStartDate)} – ${formatDateId(gapEndDate)}`
        : '';

      return {
        modalDescription: (
          <span>
            Periode yang Anda tambahkan akan berlaku mulai{' '}
            <strong>{formattedRange}</strong> dan{' '}
            <strong>menggantikan besaran subsidi</strong> pada periode aktif yang
            memiliki rentang tanggal yang sama. Dan terdapat periode tanggal{' '}
            <strong>{formattedGapRange}</strong> yang tidak memiliki kebijakan besaran
            subsidi.
          </span>
        ),
        modalTitle: `Besaran Subsidi Baru Akan Berlaku Mulai ${formattedStartDate}`,
      };
    }

    if (type === 'gap') {
      const calculatedGapStart = gapStartDate || moment(previousEndDate).add(1, 'day');
      const calculatedGapEnd = gapEndDate || moment(startDate).subtract(1, 'day');
      const formattedGapRange = formatGapRange(calculatedGapStart, calculatedGapEnd);

      return {
        modalDescription: (
          <span>
            Periode sebelumnya berakhir pada{' '}
            <strong>{formattedPreviousEndDate}</strong>, sedangkan periode baru
            dimulai pada <strong>{formattedStartDate}</strong>. Akibatnya,{' '}
            <strong>{formattedGapRange}</strong> tidak memiliki kebijakan besaran
            subsidi.
          </span>
        ),
        modalTitle: 'Akan Ada Periode Tanpa Besaran Subsidi',
      };
    }

    return {
      modalDescription: description || null,
      modalTitle: title || 'Peringatan Besaran Subsidi',
    };
  };

  const { modalTitle, modalDescription } = renderContent();

  const handleKoreksiClick = () => {
    if (onKoreksi) {
      onKoreksi();
    } else {
      onClose();
    }
  };

  return (
    <Modal
      centered
      onClose={onClose}
      opened={opened}
      radius="lg"
      size="lg"
      styles={{
        body: {
          padding: '8px 24px 24px 24px',
        },
        header: {
          alignItems: 'flex-start',
          padding: '24px 24px 8px 24px',
        },
        title: {
          color: '#1e293b',
          fontSize: '22px',
          fontWeight: 700,
          lineHeight: '1.3',
          paddingRight: '12px',
        },
      }}
      title={modalTitle}
      transitionProps={{ duration: 0 }}
      withCloseButton
    >
      <div>
        <div
          style={{
            color: '#475569',
            fontSize: '15px',
            lineHeight: '1.6',
            marginBottom: '28px',
          }}
        >
          {modalDescription}
        </div>

        <div className={styles.modalFooter}>
          <button
            className={styles.btnOutlineGreen}
            disabled={isLoading}
            onClick={handleKoreksiClick}
            style={{ borderRadius: '8px', flex: 1, padding: '10px 24px' }}
            type="button"
          >
            {koreksiText}
          </button>
          <button
            className={styles.btnPrimaryGreen}
            disabled={isLoading}
            onClick={onConfirm}
            style={{
              backgroundColor: '#5E811E',
              borderRadius: '8px',
              flex: 1,
              padding: '10px 24px',
            }}
            type="button"
          >
            {isLoading ? 'Menyimpan...' : confirmText}
          </button>
        </div>
      </div>
    </Modal>
  );
}

ModalWarningSubsidi.propTypes = {
  confirmText: PropTypes.string,
  description: PropTypes.node,
  endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  gapEndDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  gapStartDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  isLoading: PropTypes.bool,
  koreksiText: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onKoreksi: PropTypes.func,
  opened: PropTypes.bool.isRequired,
  previousEndDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  startDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  title: PropTypes.node,
  type: PropTypes.oneOf(['replace', 'gap', 'replace_gap', 'custom']),
};
